<script setup>
import { ref, getCurrentInstance } from 'vue'
import { DriverStore } from '../pinia/Driver.js'

const { proxy } = getCurrentInstance()
const Driver = DriverStore()

const currency = ref('d')
const amount = ref(0)
const recipient = ref('')
const snackbar = ref(false)
const error = ref('')
const recipientRules = [
  v => /^[a-zA-Z0-9]*$/.test(v) || 'Only letters and numbers are allowed'
]

async function save() {
  error.value = ''

  if (amount.value <= 0) {
    error.value = 'Amount must be greater than 0'
    return
  }

  if (recipient.value.length === 0) {
    error.value = 'Please enter a recipient name'
    return
  }

  if (recipientRules[0](recipient.value) !== true) {
    error.value = recipientRules[0](recipient.value)
    return
  }

  try {
    await proxy.$http.post("", JSON.stringify({
      serviceName: "account",
      methodName: "money",
      parameters: [currency.value, amount.value, recipient.value]
    }))

    if (currency.value == "d")
      Driver.persona.cash = Driver.persona.cash - amount.value
    else
      Driver.persona.boost = Driver.persona.boost - amount.value

    currency.value = "d"
    amount.value = 0
    recipient.value = ''
    snackbar.value = true
  } catch (err) {
    error.value = err.response?.data?.error || 'Transaction failed'
  }
}

function filterRecipientInput() {
  recipient.value = recipient.value.replace(/[^a-zA-Z0-9]/g, '')
}
</script>

<template><div class="settings-tab-page konopabankForm">
  <v-snackbar :timeout="2000" color="success" elevation="24" v-model="snackbar">
      Your transaction is successful.
  </v-snackbar>

  <div class="settings-tab-header">
    <div class="header-icon">
      <v-icon size="28">mdi-bank</v-icon>
    </div>
    <div class="header-content">
      <h3 class="page-title">Konopa Bank</h3>
      <p class="page-subtitle">Send money to other drivers</p>
    </div>
  </div>

  <v-alert class="error" v-if="error" :text="error" type="error"></v-alert>

  <v-alert v-if="Driver.persona.level < 15" color="warning" icon="mdi-lock" class="mb-4">
    <div class="requirement-notice">
      <div class="notice-title">Account Transfer in Progress</div>
      <div class="notice-text">
        You are new in the city. Your bank account is currently in the process of transfer. Please come back later.
        <br><br>
        <strong>Level 15 required for the sender and recipient</strong>
      </div>
    </div>
  </v-alert>

  <v-form v-if="Driver.persona.level >= 15">
    <div class="section-list">
      <div class="section-row">
        <div class="section-body">
          <div class="setting-item">
            <div class="setting-info full-width">
              <div class="setting-label">
                <v-icon size="20" class="mr-2">mdi-cash-multiple</v-icon>
                Select Currency
              </div>
              <div class="currency-selection">
                <div class="currency-card" :class="{'currency-selected': currency === 'd'}" @click="currency = 'd'">
                  <v-icon size="28" class="currency-icon">mdi-currency-usd</v-icon>
                  <div class="currency-label">Cash</div>
                  <div class="currency-amount">{{ number_format(Driver.persona.cash) }} $</div>
                </div>
                <div class="currency-card" :class="{'currency-selected': currency === 'b'}" @click="currency = 'b'">
                  <v-icon size="28" class="currency-icon">mdi-rocket-launch</v-icon>
                  <div class="currency-label">Speed Boost</div>
                  <div class="currency-amount">{{ number_format(Driver.persona.boost) }} SB</div>
                </div>
              </div>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="setting-item">
            <div class="setting-info full-width">
              <div class="setting-label">
                <v-icon size="20" class="mr-2">mdi-calculator</v-icon>
                Amount
              </div>
              <div class="setting-description">
                How much would you like to send?
              </div>
              <v-text-field v-model="amount" type="number" min="0" max="1500000" placeholder="Enter amount" variant="outlined" density="comfortable" hide-details class="setting-input"></v-text-field>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="setting-item">
            <div class="setting-info full-width">
              <div class="setting-label">
                <v-icon size="20" class="mr-2">mdi-account-arrow-right</v-icon>
                Recipient
              </div>
              <div class="setting-description">
                Enter the driver name (alphanumeric only)
              </div>
              <v-text-field v-model="recipient" placeholder="Recipient driver name" variant="outlined" density="comfortable" hide-details :rules="recipientRules" @input="filterRecipientInput" class="setting-input"></v-text-field>
            </div>
          </div>

          <v-btn class="save-btn send-btn" variant="elevated" color="primary" size="large" @click="save" block>
            <v-icon class="mr-2">mdi-send</v-icon>
            Send Transaction
          </v-btn>
        </div>
      </div>
    </div>
  </v-form>
</div></template>

<style scoped>
.error {
  margin-bottom:16px}
.currency-selection {
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
  margin-top:8px}
.currency-card {
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:8px;
  padding:20px;
  background:var(--glass);
  border:2px solid rgba(255,255,255,.1);
  border-radius:12px;
  cursor:pointer;
  transition:all .2s ease}
.currency-card:hover {
  background:var(--glass-strong);
  border-color:rgba(var(--v-theme-primary),.3);
  transform:translateY(-2px)}
.currency-card.currency-selected {
  background:rgba(var(--v-theme-primary),.15);
  border-color:rgb(var(--v-theme-primary));
  box-shadow:0 0 16px rgba(var(--v-theme-primary),.3)}
.currency-icon {
  color:rgb(var(--v-theme-primary))}
.currency-label {
  font-size:.9em;
  font-weight:500;
  color:var(--ink-soft)}
.currency-amount {
  font-size:1.1em;
  font-weight:700;
  color:var(--ink)}
.requirement-notice {
  text-align:left}
.notice-title {
  font-size:1em;
  font-weight:600;
  margin-bottom:8px}
.notice-text {
  font-size:.9em;
  line-height:1.5}
.send-btn {
  margin-top:4px}
</style>
